import { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { NewsletterService, formData } from '@ts/services/newsletterService';
import { SubmitButton } from '../SubmitButton/SubmitButton';
import { Result } from './NewsletterSignUp';

type Props = {
  signUpError: boolean;
  handleSignUpMessage: (result?: Result) => void;
  newsletterService: NewsletterService;
  newsletterBtnName?: string;
};

/**
 * Handles Newsletter Form Submission and Feedback Message
 */
export const NewsletterForm = ({
  signUpError,
  handleSignUpMessage,
  newsletterService,
  newsletterBtnName,
}: Props) => {
  const [clickOutside, setClickOutside] = useState(false);

  const [showDataPolicy, setShowDataPolicy] = useState(false);
  const [formData, setFormData] = useState<formData>({
    EMAIL: '',
    OPT_IN: 1,
    CONFIRM_EMAIL: false,
  });

  const handleChange = (event: JSX.TargetedEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;

    setFormData(oldFormData => ({
      ...oldFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async (
    event: JSX.TargetedEvent<HTMLFormElement, Event>,
  ) => {
    event.preventDefault();

    if (formData.CONFIRM_EMAIL) {
      // The honeypot field was filled, so we assume it's a bot and don't send the request - but still show success message
      handleSignUpMessage();
      return;
    }

    try {
      await newsletterService.signUp(formData);
      handleSignUpMessage();
    } catch (error) {
      if (error) handleSignUpMessage('error');
    }
  };

  return (
    <form className="bc-newsletter__form" onSubmit={handleSubmit}>
      <input
        className="bc-newsletter__form--input"
        type="email"
        name="EMAIL"
        placeholder="Ihre E-Mail-Adresse"
        aria-label="E-Mail-Adresse"
        onFocus={() => setShowDataPolicy(true)}
        onInput={handleChange}
        required
        data-testid="email-input"
      />

      <SubmitButton
        label={newsletterBtnName || 'Abonnieren'}
        sizeVariant="lg"
        setClickOutside={setClickOutside}
        stylingClass="bc-newsletter__form--submit"
      />

      {showDataPolicy && (
        <span
          className="bc-newsletter__form--data-policy"
          data-testid="data-policy"
        >
          Mit Absenden des Formulars akzeptieren Sie die{' '}
          <a href="/datenschutz">Datenschutzerklärung</a>.
        </span>
      )}

      {!clickOutside && signUpError && (
        <div className="bc-newsletter__form--error" data-testid="signup-error">
          Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.
        </div>
      )}

      <div className="u-a11y-hidden">
        {/* This is a honeypot field to trick spam bots */}
        <input
          type="email"
          name="CONFIRM_EMAIL"
          autoComplete="off"
          tabIndex={-1}
        />
      </div>
    </form>
  );
};
